import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Holding, Currency, Loan, LiquidityEvent } from '../types';
import { KPISection } from '../components/KPISection';
import { AllocationCharts } from '../components/AllocationCharts';
import { DashboardCharts } from '../components/DashboardCharts';
import { Button } from '../components/ui/Button';
import { RefreshCw, CheckCircle2, FlaskConical, X } from 'lucide-react';
interface DashboardPageProps {
  holdings: Holding[];
  baseCurrency: Currency;
  partnerNames: [string, string];
  loans?: Loan[];
  liquidityEvents?: LiquidityEvent[];
  monthlyContribution?: number;
  onRecalculate?: () => number;
  isDemo?: boolean;
  onDismissDemo?: () => void;
}
export function DashboardPage({
  holdings,
  baseCurrency,
  partnerNames,
  loans = [],
  liquidityEvents = [],
  monthlyContribution = 0,
  onRecalculate,
  isDemo = false,
  onDismissDemo
}: DashboardPageProps) {
  const [recalcStatus, setRecalcStatus] = useState<{
    show: boolean;
    corrections: number;
    spinning: boolean;
  }>({
    show: false,
    corrections: 0,
    spinning: false
  });
  // Filter out inactive holdings for all dashboard calculations
  const activeHoldings = useMemo(() => holdings.filter(h => h.isActive ?? true), [holdings]);
  const handleRecalculate = () => {
    if (!onRecalculate) return;
    setRecalcStatus({
      show: false,
      corrections: 0,
      spinning: true
    });
    // Small delay to show the spin animation
    setTimeout(() => {
      const corrections = onRecalculate();
      setRecalcStatus({
        show: true,
        corrections,
        spinning: false
      });
      // Auto-hide after 4 seconds
      setTimeout(() => {
        setRecalcStatus(prev => ({
          ...prev,
          show: false
        }));
      }, 4000);
    }, 300);
  };
  return <div className="space-y-8" data-id="element-1613">
      {/* Demo Data Banner */}
      {isDemo && <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-1614">
          <div className="flex items-center gap-2.5 min-w-0" data-id="element-1615">
            <FlaskConical className="w-4 h-4 text-amber-600 shrink-0" data-id="element-1616" />
            <p className="text-sm text-amber-800" data-id="element-1617">
              You're exploring with sample data.{' '}
              <Link to="/settings" className="font-semibold text-amber-700 underline underline-offset-2 decoration-amber-300 hover:text-amber-900 hover:decoration-amber-500 transition-colors" data-id="element-1618">
                Reset & start with your own portfolio →
              </Link>
            </p>
          </div>
          {onDismissDemo && <button onClick={onDismissDemo} className="p-1 rounded-md text-amber-400 hover:text-amber-600 hover:bg-amber-100 transition-colors shrink-0" aria-label="Dismiss banner" data-id="element-1619">
              <X className="w-4 h-4" data-id="element-1620" />
            </button>}
        </div>}

      {/* Dashboard Header with Recalculate */}
      {onRecalculate && <div className="flex items-center justify-between" data-id="element-1621">
          <div data-id="element-1622">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-1623">Dashboard</h1>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-1624">
              Portfolio overview and key metrics
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-1625">
            {recalcStatus.show && <div className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-all ${recalcStatus.corrections > 0 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-1626">
                <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1627" />
                {recalcStatus.corrections > 0 ? `${recalcStatus.corrections} correction${recalcStatus.corrections !== 1 ? 's' : ''} applied` : 'All balances verified ✓'}
              </div>}
            <Button variant="secondary" size="sm" onClick={handleRecalculate} disabled={recalcStatus.spinning} className="gap-1.5" data-id="element-1628">
              <RefreshCw className={`w-3.5 h-3.5 ${recalcStatus.spinning ? 'animate-spin' : ''}`} data-id="element-1629" />
              Recalculate
            </Button>
          </div>
        </div>}

      <section data-id="element-1630">
        <KPISection holdings={activeHoldings} baseCurrency={baseCurrency} partnerNames={partnerNames} loans={loans} data-id="element-1631" />
      </section>

      <section data-id="element-1632">
        <AllocationCharts holdings={activeHoldings} baseCurrency={baseCurrency} partnerNames={partnerNames} loans={loans} data-id="element-1633" />
      </section>

      <section data-id="element-1634">
        <DashboardCharts holdings={activeHoldings} loans={loans} liquidityEvents={liquidityEvents} baseCurrency={baseCurrency} partnerNames={partnerNames} monthlyContribution={monthlyContribution} data-id="element-1635" />
      </section>
    </div>;
}